/**
 * Currency formatting utilities using Intl.NumberFormat
 */

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const USD_FORMATTER_NO_CENTS = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Format a number as USD currency with proper thousands separators
 * @param amount - The amount to format (in dollars, not cents)
 * @param options - Formatting options
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrency(
  amount: number,
  options?: {
    /** Hide cents if the amount is a whole number */
    hideCentsForWholeNumbers?: boolean;
    /** Always hide cents */
    hideCents?: boolean;
  },
): string {
  if (options?.hideCents) {
    return USD_FORMATTER_NO_CENTS.format(amount);
  }

  if (options?.hideCentsForWholeNumbers && Number.isInteger(amount)) {
    return USD_FORMATTER_NO_CENTS.format(amount);
  }

  return USD_FORMATTER.format(amount);
}

/**
 * Format cents as USD currency with proper thousands separators
 * @param amountInCents - The amount in cents
 * @param options - Formatting options
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrencyFromCents(
  amountInCents: number,
  options?: {
    /** Hide cents if the amount is a whole number */
    hideCentsForWholeNumbers?: boolean;
    /** Always hide cents */
    hideCents?: boolean;
  },
): string {
  return formatCurrency(amountInCents / 100, options);
}

/**
 * Format a number with thousands separators (without currency symbol)
 * @param amount - The amount to format
 * @returns Formatted number string (e.g., "1,234.56")
 */
export function formatNumber(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format an integer with thousands separators (no decimal places)
 * @param amount - The amount to format
 * @returns Formatted number string (e.g., "1,234")
 */
export function formatInteger(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
